import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";
import type {
  CreateForeignNationalReminderRequest,
  ForeignNationalReminder,
  LawFirmDueReminder,
  UpdateForeignNationalReminderRequest
} from "./types";

const BASE_URL = "/law_firms";

/**
 * A firm reminder with the client's name resolved server-side. A firm entry
 * about no one client has no `foreign_national_id` (and no `client_name`).
 */
export type LawFirmReminder = Omit<
  ForeignNationalReminder,
  "foreign_national_id"
> & {
  foreign_national_id: string | null;
  client_name?: string | null;
};

export type LawFirmReminderQuery = {
  /** ISO instant or `YYYY-MM-DD`; reminders ending at or after this. */
  from?: string;
  /** ISO instant or `YYYY-MM-DD` (inclusive day); reminders starting by this. */
  to?: string;
  /** Only reminders assigned to this user. */
  assignee?: string;
};

export const LawFirmRemindersAPI = {
  /** Every reminder in the firm, optionally narrowed to a window/assignee. */
  async list(query: LawFirmReminderQuery = {}): Promise<LawFirmReminder[]> {
    try {
      const response = await axiosInstance.get<LawFirmReminder[]>(
        `${BASE_URL}/reminders`,
        { params: query }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** A firm entry about no one client (assigned to the caller by default). */
  async create(
    payload: CreateForeignNationalReminderRequest
  ): Promise<LawFirmReminder> {
    try {
      const response = await axiosInstance.post<LawFirmReminder>(
        `${BASE_URL}/reminders`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async update(
    reminderId: string,
    patch: UpdateForeignNationalReminderRequest
  ): Promise<LawFirmReminder> {
    try {
      const response = await axiosInstance.put<LawFirmReminder>(
        `${BASE_URL}/reminders/${reminderId}`,
        patch
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async remove(reminderId: string): Promise<{ message?: string }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/reminders/${reminderId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listDueForLawFirm(windowHours = 24): Promise<LawFirmDueReminder[]> {
    try {
      const response = await axiosInstance.get<LawFirmDueReminder[]>(
        `${BASE_URL}/reminders/due`,
        {
          params: { window_hours: windowHours }
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listDueForMine(windowHours = 24): Promise<LawFirmDueReminder[]> {
    try {
      const response = await axiosInstance.get<LawFirmDueReminder[]>(
        `${BASE_URL}/reminders/due/mine`,
        {
          params: { window_hours: windowHours }
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
